import { useState, useEffect, useCallback } from 'react';
import { getCustomersWithAlarms } from '../../../services/bureauManagement/bureauManagement.service';
import type { BureauCustomerTreeNode } from '../../../services/bureauManagement/bureauManagement.types';
import type { SiteTreeNode } from '../../../services/siteManagement/siteManagement.types';

interface UseBureauCustomersTreeResult {
  nodes: SiteTreeNode[] | null;
  unacknowledgedCounts: Record<string, number> | undefined;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * Hook that fetches the bureau customers tree with alarm counts.
 * Returns pre-filtered tree (only sites with alarms) and pre-calculated badge counts.
 * This replaces the complex useAllowedSiteMapping logic.
 */
export function useBureauCustomersTree(): UseBureauCustomersTreeResult {
  const [nodes, setNodes] = useState<SiteTreeNode[] | null>(null);
  const [unacknowledgedCounts, setUnacknowledgedCounts] = useState<Record<string, number> | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  const refresh = useCallback(() => setRefreshTrigger(prev => prev + 1), []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        // Call without any parameters to get all alarms (including toBeRemoved)
        const response = await getCustomersWithAlarms();

        if (!mounted) return;

        if (!response.success || !response.data) {
          throw new Error(response.error?.message || 'Failed to fetch bureau customers tree');
        }

        const customers = response.data.customers || [];

        // Convert BureauCustomerTreeNode[] to SiteTreeNode[] format
        // ONLY include customers and sites with activeNotAck > 0
        const treeNodes: SiteTreeNode[] = customers
          .map((customer: BureauCustomerTreeNode) => {
            // Filter sites to only include those with unacknowledged alarms
            const sitesWithUnackAlarms = customer.children?.filter(
              (site: BureauCustomerTreeNode) => site.activeNotAck > 0
            ) || [];

            // Skip customer if no sites have unacknowledged alarms
            if (sitesWithUnackAlarms.length === 0) {
              return null;
            }

            return {
              key: customer.key,
              title: customer.title,
              type: customer.type,
              sirId: customer.sirId ?? null,
              siteIdentifier: customer.siteIdentifier ?? null,
              children: sitesWithUnackAlarms.map((site: BureauCustomerTreeNode) => ({
                key: site.key,
                title: site.title,
                type: site.type,
                sirId: site.sirId ?? null,
                siteIdentifier: site.siteIdentifier ?? null,
                siteGuid: site.siteGuid ?? null,
                children: site.children || [],
              })),
            };
          })
          .filter((node): node is SiteTreeNode => node !== null);

        // Build unacknowledgedCounts from activeNotAck field
        const counts: Record<string, number> = {};
        
        customers.forEach((customer: BureauCustomerTreeNode) => {
          // Customer-level count (only if > 0)
          if (customer.activeNotAck > 0) {
            counts[customer.key] = customer.activeNotAck;
          }
          
          // Site-level counts (only if > 0)
          customer.children?.forEach((site: BureauCustomerTreeNode) => {
            if (site.activeNotAck > 0) {
              counts[site.key] = site.activeNotAck;
            }
          });
        });

        setNodes(treeNodes);
        setUnacknowledgedCounts(Object.keys(counts).length > 0 ? counts : undefined);
        setLoading(false);
      } catch (err) {
        if (!mounted) return;
        console.info('[useBureauCustomersTree] Error fetching tree:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setNodes(null);
        setUnacknowledgedCounts(undefined);
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [refreshTrigger]);

  return {
    nodes,
    unacknowledgedCounts,
    loading,
    error,
    refresh,
  };
}

export default useBureauCustomersTree;
